/**
 * WAM → InstrumentPlugin adapter.
 *
 * WAMs handle polyphony internally via MIDI events, which doesn't
 * match our per-voice-per-trigger `InstrumentPlugin` contract 1:1.
 * The reconciliation:
 *
 *   - ONE WAM instance per (ctx, instrumentId) — lazily created on
 *     first `createVoice`, cached for the ctx's lifetime.
 *   - Every voice handle shares the same `output: AudioNode` (the
 *     instance's `audioNode`). Web Audio treats repeated
 *     `connect(src, dest)` calls as idempotent, so multiple voice
 *     connects from the shared node to the same bus land on one edge.
 *   - `voice.trigger(when, vel, pitch)` sends a note-on MIDI event
 *     via `scheduleEvents({ type: 'wam-midi' })`. Pitch is
 *     resolved against the descriptor's `baseMidiNote` (default 60
 *     = middle C) so pitch offsets from our engine map to MIDI
 *     notes the WAM understands.
 *   - `voice.release(when)` sends the matching note-off. Note number
 *     is cached per-voice so the release targets the correct note
 *     even if other voices are in flight.
 *   - `voice.dispose()` is a NO-OP — the shared instance outlives
 *     individual voices. Session teardown (or a future
 *     pad-removal hook) is the only thing that destroys instances.
 *
 * Known limitations (acceptable for v1, documented for follow-ups):
 *
 *   - Bus reroute mid-session: if a pad's bus changes while the
 *     WAM is in use, the instance accumulates connections to both
 *     old and new dest. Inaudible (Web Audio spec: equal signal
 *     fan-out), not a correctness issue, but means the old bus
 *     keeps receiving the WAM's output. Fixable with an explicit
 *     bus-change hook at the engine level.
 *   - Dispose never runs: we don't destroy the cached instance
 *     during session. Next page reload or context teardown clears
 *     it. Acceptable — a typical WAM is a few KB of AudioWorklet
 *     state.
 */

import type {
  InstrumentPlugin,
  InstrumentPresetValue,
  InstrumentVoiceHandle,
  ParamSchema,
} from '@/audio/plugins/types'
import {
  getPreloadedWamClass,
  getWamHostGroupId,
  type WamClass,
  type WamInstance,
} from './bootstrap'
import { noteOffBytes, noteOnBytes } from './midiEvents'

export interface WamInstrumentDescriptor {
  /** Registry id — MUST start with `wam-` so the Plugin-tab resolver
   *  routes to `<WamGui>` instead of a React view. */
  id: string
  label: string
  category: InstrumentPlugin['category']
  /** Source URL — must have been preloaded via `preloadWam` during
   *  engine boot. Used here as the cache key. */
  url: string
  /** MIDI note the descriptor treats as "pitch 0" — pitch offsets
   *  from our engine (semitones relative to the pad's base) are
   *  added to this to produce the MIDI note sent to the WAM.
   *  Default 60 (middle C). */
  baseMidiNote?: number
  /** Channel (0-15) for note events. Default 0. Most monotimbral
   *  WAM synths ignore the channel; multitimbral ones read it to
   *  target a specific patch slot. */
  channel?: number
  /** Static param schema surfaced in compact Cell-Editor grids —
   *  the real editor is the WAM's Custom-Element GUI mounted in the
   *  Plugin tab. Pass `[]` if the WAM's params should only be
   *  editable through its native surface. */
  paramSchema?: readonly ParamSchema[]
  defaultPreset?: Record<string, InstrumentPresetValue>
}

/**
 * Cache of live instances keyed by a `${ctx-id}:${instrumentId}`
 * composite. `ctx-id` comes from the host-group id (one per
 * AudioContext), so live + offline render contexts get distinct
 * instances even for the same instrument.
 */
const instanceByKey = new Map<string, Promise<WamInstance | null>>()

function cacheKey(ctx: BaseAudioContext, instrumentId: string): string {
  // getWamHostGroupId throws if init hasn't run; wrap to a safe
  // sentinel so the adapter can still construct a handle that
  // reports the error rather than crashing the whole trigger path.
  let hostKey: string
  try {
    hostKey = getWamHostGroupId(ctx)
  } catch {
    hostKey = '__uninit__'
  }
  return `${hostKey}:${instrumentId}`
}

async function getOrCreateInstance(
  ctx: BaseAudioContext,
  instrumentId: string,
  wamClass: WamClass,
): Promise<WamInstance | null> {
  const key = cacheKey(ctx, instrumentId)
  const existing = instanceByKey.get(key)
  if (existing) return existing
  let hostGroupId: string
  try {
    hostGroupId = getWamHostGroupId(ctx)
  } catch {
    console.warn(
      `[wam] ${instrumentId}: host group not initialised; cannot create instance`,
    )
    return null
  }
  const promise = wamClass.createInstance(hostGroupId, ctx).catch(err => {
    console.warn(`[wam] ${instrumentId} createInstance failed:`, err)
    instanceByKey.delete(key)
    return null
  })
  instanceByKey.set(key, promise)
  return promise
}

export function createWamInstrumentPlugin(
  descriptor: WamInstrumentDescriptor,
): InstrumentPlugin {
  const baseMidiNote = descriptor.baseMidiNote ?? 60
  const channel = descriptor.channel ?? 0
  const defaultPreset = descriptor.defaultPreset ?? {}
  const paramSchema = descriptor.paramSchema ?? []

  return {
    id: descriptor.id,
    label: descriptor.label,
    category: descriptor.category,
    defaultPreset,
    paramSchema: [...paramSchema],
    createVoice(ctx, _preset = {}): InstrumentVoiceHandle {
      return buildWamInstrumentVoice(ctx, descriptor, baseMidiNote, channel)
    },
  }
}

function buildWamInstrumentVoice(
  ctx: AudioContext,
  descriptor: WamInstrumentDescriptor,
  baseMidiNote: number,
  channel: number,
): InstrumentVoiceHandle {
  // Stable output node — a Gain pass-through that we'll wire the
  // shared WAM instance into once it resolves. Using a per-voice
  // wrapper (rather than exposing `instance.audioNode` directly) lets
  // the host connect idempotently without us exposing a null
  // placeholder output before the async instance lands.
  const output = ctx.createGain()
  const wamClass = getPreloadedWamClass(descriptor.url)

  // Per-voice note state — captured for the release path.
  let activeNote: number | null = null
  let pendingTrigger: {
    when: number
    velocity: number
    pitchSemitones: number
  } | null = null
  let pendingRelease: { when: number; velocity: number } | null = null
  let instance: WamInstance | null = null
  let disposed = false

  if (!wamClass) {
    console.warn(
      `[wam] ${descriptor.id}: no preloaded class for ${descriptor.url}; voice is silent`,
    )
    // Return a voice that accepts trigger/release/dispose but
    // produces no audio. Matches the effect adapter's fail-soft
    // behaviour so a broken WAM URL doesn't crash the engine.
    return {
      output,
      trigger() {
        /* no-op — silent voice */
      },
      release() {
        /* no-op */
      },
      dispose() {
        try {
          output.disconnect()
        } catch {
          /* already */
        }
      },
    }
  }

  // Kick off (or reuse) the shared instance. When it resolves we
  // wire it behind our voice output + apply any buffered trigger.
  void getOrCreateInstance(ctx, descriptor.id, wamClass).then(created => {
    if (disposed) return
    if (!created) return
    instance = created
    try {
      created.audioNode.connect(output)
    } catch {
      /* already */
    }
    if (pendingTrigger) {
      sendNoteOn(
        created,
        baseMidiNote + pendingTrigger.pitchSemitones,
        pendingTrigger.velocity,
        pendingTrigger.when,
        channel,
      )
      activeNote = baseMidiNote + pendingTrigger.pitchSemitones
      pendingTrigger = null
    }
    if (pendingRelease && activeNote !== null) {
      sendNoteOff(
        created,
        activeNote,
        pendingRelease.velocity,
        pendingRelease.when,
        channel,
      )
      activeNote = null
      pendingRelease = null
    }
  })

  return {
    output,
    trigger(when, velocity, pitchSemitones) {
      if (disposed) return
      if (!instance) {
        // Buffer — the then-handler above will flush on resolve.
        pendingTrigger = { when, velocity, pitchSemitones }
        return
      }
      const noteNum = baseMidiNote + pitchSemitones
      sendNoteOn(instance, noteNum, velocity, when, channel)
      activeNote = noteNum
    },
    release(when) {
      if (disposed) return
      if (!instance) {
        pendingRelease = { when, velocity: 0.5 }
        return
      }
      if (activeNote === null) return
      sendNoteOff(instance, activeNote, 0.5, when, channel)
      activeNote = null
    },
    dispose() {
      disposed = true
      // Don't destroy the shared instance. Do disconnect the voice's
      // output wrapper so the host's chain-disconnect cleans up.
      try {
        output.disconnect()
      } catch {
        /* already */
      }
    },
  }
}

function sendNoteOn(
  instance: WamInstance,
  note: number,
  velocity: number,
  when: number,
  channel: number,
): void {
  const clamped = Math.max(0, Math.min(127, Math.round(velocity * 127)))
  try {
    instance.audioNode.scheduleEvents({
      type: 'wam-midi',
      time: when,
      data: { bytes: noteOnBytes(note, clamped, channel) },
    })
  } catch (err) {
    console.warn('[wam] note-on scheduleEvents failed:', err)
  }
}

function sendNoteOff(
  instance: WamInstance,
  note: number,
  velocity: number,
  when: number,
  channel: number,
): void {
  const clamped = Math.max(0, Math.min(127, Math.round(velocity * 127)))
  try {
    instance.audioNode.scheduleEvents({
      type: 'wam-midi',
      time: when,
      data: { bytes: noteOffBytes(note, clamped, channel) },
    })
  } catch (err) {
    console.warn('[wam] note-off scheduleEvents failed:', err)
  }
}

/** Test-only: clear the instance cache. */
export function __resetWamInstrumentCacheForTests(): void {
  instanceByKey.clear()
}
